import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axiosInstance from "../../../../Services/axiosCommon";
import { BackButton, EditButton } from "../../../../Components/Utils/Buttons";
import NuDate from "../../../../Components/Utils/NuDate";
import { NormalHeading } from "../../../../Components/Utils/Heading";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import {
  Calendar,
  Phone,
  Mail,
  User,
  Briefcase,
  GraduationCap,
  CheckCircle,
  XCircle,
  Code,
  UserCircle,
} from "lucide-react";
import { useToast } from "../../../../Components/Others/ToastServices";
import { GetAccess } from "../../../../Components/Utils/roles";

function ViewTeam() {
  const { teamViewId } = useParams();
  const location = useLocation();
  const from = location.state?.from;
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();
 const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;

  function getView() {
    setIsLoading(true);
    axiosInstance
      .get(`/team/${teamViewId}`)
      .then((res) => {
        setData(res.data?.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching team data:", err);
        toast.open({ type: "error", message: "Failed to fetch team member data" });
        setIsLoading(false);
      });
  }

  useEffect(() => {
    if (teamViewId) {
      getView();
    }
  }, [teamViewId]);

  return (
   <div className="px-4 py-2 w-full h-full">
  <div className="w-full mb-4 flex justify-between items-center">
    <Breadcrumb
      items={[
        { name: "Team", link: "/teammember/team" },
        { name: "Profile" },
      ]}
    />
   <div className=" flex gap-2 justify-end">
             <Link
               to={`/teammember/team`}
               state={{
                 currentPage: pageIndex,
                 searchQuery: searchQuery != "" ? searchQuery : "",
               }}
             >
               <BackButton />
             </Link>
             {GetAccess("client_tickets_edit")&&
             <Link
               to={`/teammember/team/edit/${teamViewId}`}
               state={{
                 data: data,
                 from: `/teammember/team/view/${teamViewId}`,
               }}
             >
               <EditButton height="8" />
             </Link>}
           </div>
  </div>

  {/* Main Card */}
  <div className="bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col lg:flex-row h-[calc(100vh-145px)]">
    {/* Sidebar/Profile */}
    <div className="w-full h-[60%] md:h-full lg:w-1/3 bg-gradient-to-br from-red-600 to-red-400 text-white p-8 flex flex-col items-center justify-center">
      {data?.image?.file ? (
        <img
          src={data.image.file}
          alt={data?.name}
          className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-white shadow-lg"
        />
      ) : (
        <div className="flex items-center justify-center w-32 h-32 md:w-40 md:h-40 rounded-full bg-red-300 text-white text-2xl font-semibold">
          <UserCircle size={64} />
        </div>
      )}
      <h2 className="mt-4 md:mt-6 text-2xl md:text-3xl font-bold">{data?.name || "N/A"}</h2>
      <p className="text-sm opacity-80">{data?.designation || "No Designation"}</p>
      <span className="mt-3 px-4 py-1 bg-white text-red-600 rounded-full text-xs font-medium">
        {data?.division || "No Division Assigned"}
      </span>
    </div>

    {/* Details */}
    <div className="w-full lg:w-2/3 p-6 md:p-8 space-y-6 overflow-y-scroll">
      {/* Personal */}
      <div>
        <NormalHeading title="Personal Information" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Info label="Date of Birth" icon={<Calendar size={20} />} value={data?.dob ? <NuDate value={data.dob} format="DD-MMM-YYYY" /> : "N/A"} />
          <Info label="Gender" icon={<User size={20} />} value={data?.gender || "N/A"} />
          <Info label="Email" icon={<Mail size={20} />} value={data?.email || "N/A"} />
          <Info label="Phone" icon={<Phone size={20} />} value={data?.phone ? `${data?.countryCode || ""} ${data?.phone}` : "N/A"} />
        </div>
      </div>

      {/* Employment */}
      <div>
        <NormalHeading title="Employment Details" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Info label="Division" icon={<Briefcase size={20} />} value={data?.division || "N/A"} />
          <Info label="Designation" icon={<Briefcase size={20} />} value={data?.designation || "N/A"} />
          <Info label="Date of Join" icon={<Calendar size={20} />} value={data?.dateOfJoin ? <NuDate value={data.dateOfJoin} format="DD-MMM-YYYY" /> : "N/A"} />
          <Info label="Date Relieved" icon={<Calendar size={20} />} value={data?.dateOfRelieved ? <NuDate value={data.dateOfRelieved} format="DD-MMM-YYYY" /> : "N/A"} />
          <Info label="Status" icon={data?.status === "Active" ? <CheckCircle size={20} className="text-green-500" /> : <XCircle size={20} className="text-red-500" />} value={
            <span className={`px-3 py-0.5 rounded-full text-xs font-medium ${data?.status === "Active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              {data?.status || "N/A"}
            </span>
          } />
        </div>
      </div>

      {/* Professional */}
      <div>
        <NormalHeading title="Professional Details" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Info label="Qualifications" icon={<GraduationCap size={20} />} value={Array.isArray(data?.qualifications) && data.qualifications.length > 0 ? (
            <ul className="list-disc list-inside text-xs text-gray-800 space-y-1">
              {data.qualifications.map((qualification, index) => (
                <li key={index}>{qualification}</li>
              ))}
            </ul>
          ) : "N/A"} />
          <Info label="Skills" icon={<Code size={20} />} value={Array.isArray(data?.skills) && data.skills.length > 0 ? (
            <ul className="list-disc list-inside text-xs text-gray-800 space-y-1">
              {data.skills.map((skill, index) => (
                <li key={index}>{skill}</li>
              ))}
            </ul>
          ) : "N/A"} />
        </div>
      </div>
    </div>
  </div>
</div>

  );
}

// Info component with icons
const Info = ({ label, value, icon }) => (
  <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg shadow-sm hover:bg-gray-100 transition duration-200">
    <div className="text-red-500">{icon}</div>
    <div className="flex flex-col">
      <span className="text-gray-500 font-semibold text-xs">{label}</span>
      <span className="text-gray-800 text-xs">{value}</span>
    </div>
  </div>
);

export default ViewTeam;